import { motion } from 'framer-motion';
import { RefreshCw, X, Heart, Check, Plus, RotateCcw } from 'lucide-react';
import { formatMacro } from '../utils/nutritionFormat';
import { useMealImageSrc } from './MealImage';

/**
 * Unified Meal Card component for Home and Plan views
 * 
 * Props:
 * - meal: { meal_id, name, calories, protein, carbs, fat, image_url? }
 * - planMacros: { calories, protein, carbs, fat } - fallback macros from plan (for custom meals)
 * - mealType: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack'
 * - planId: string (for action callbacks)
 * - status: 'pending' | 'completed' | 'skipped'
 * - isFavorite: boolean
 * - variant: 'home' | 'plan' - determines button configuration
 *   - 'home': Log/Undo, Skip/Remove, Favorite (Remove shown for skipped meals)
 *   - 'plan': Swap, Remove, Favorite
 * - onCardClick: () => void
 * - onLog: (e) => void - Log meal (for home)
 * - onUndo: (e) => void - Undo log, return to pending (for home)
 * - onSkip: (e) => void - Skip meal (for home)
 * - onRemove: (e) => void - Remove meal from plan (for both home/skipped and plan)
 * - onSwap: (e) => void - Swap meal (for plan)
 * - onFavorite: (e) => void
 * - isLoading: { log?: boolean, undo?: boolean, skip?: boolean, swap?: boolean, remove?: boolean, favorite?: boolean }
 * - showLoggedBadge: boolean
 */
function MealCard({
  meal,
  planMacros,
  mealType,
  planId,
  status = 'pending',
  isFavorite = false,
  variant = 'home',
  onCardClick,
  onLog,
  onUndo,
  onSkip,
  onSwap,
  onRemove,
  onFavorite,
  isLoading = {},
  showLoggedBadge = false,
  leftoverRole = null,
}) {
  // Resolve private AI-photo markers via the authenticated resolver. Called BEFORE any early return
  // so the hook order stays stable (Rules of Hooks). Normal https/data URLs pass through unchanged.
  const resolvedSrc = useMealImageSrc(meal?.image_url || '');
  if (!meal) return null;

  // PREFER planMacros when available (includes computed values from backend)
  // Fall back to meal macros for meal detail views without a plan context
  // Use nullish coalescing (??) to allow legitimate 0 values
  // Always round to whole numbers for clean display
  const calories = Math.round(planMacros?.calories ?? meal?.calories ?? 0);
  const protein = formatMacro(planMacros?.protein ?? meal?.protein ?? 0);
  const carbs = formatMacro(planMacros?.carbs ?? meal?.carbs ?? 0);
  const fat = formatMacro(planMacros?.fat ?? meal?.fat ?? 0);

  // Placeholder SVG for missing images
  const getPlaceholderSvg = () => {
    const colors = {
      Breakfast: '#4A90A4',
      Lunch: '#6B8E23',
      Dinner: '#8B4513',
      Snack: '#DAA520'
    };
    const color = colors[mealType] || '#333333';
    const letter = mealType?.substring(0, 1) || 'M';
    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="${color}" width="80" height="80"/><text fill="white" font-family="Arial" font-size="32" font-weight="bold" x="50%" y="55%" text-anchor="middle">${letter}</text></svg>`)}`;
  };

  // Resolve private AI-photo markers ("/api/ai-log/photo/{meal_id}") through the authenticated resolver;
  // normal https/data URLs pass through unchanged. Empty while a marker resolves -> placeholder shows.
  const imageUrl = resolvedSrc || getPlaceholderSvg();

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    action?.(e);
  };

  const isLogged = status === 'completed';
  const isSkipped = status === 'skipped';

  return (
    <motion.div
      data-testid={`meal-card-${planId || meal.meal_id}`}
      className="bg-card border border-border/40 rounded-xl overflow-hidden transition-all hover:border-border/60"
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Card content with actions inline */}
      <div className="flex gap-3 p-3">
        {/* Left: Square thumbnail - clickable */}
        <div 
          onClick={onCardClick}
          className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-secondary/50 cursor-pointer"
        >
          <img
            src={imageUrl}
            alt={meal.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = getPlaceholderSvg();
            }}
          />
        </div>

        {/* Middle: Stacked content - clickable */}
        <div 
          onClick={onCardClick}
          className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer"
        >
          {/* Meal type + status badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {mealType}
            </span>
            {showLoggedBadge && isLogged && (
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-accent/10 text-accent text-[10px]">
                <Check size={10} />
                Logged
              </span>
            )}
            {isSkipped && (
              <span className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px]">
                Skipped
              </span>
            )}
            {leftoverRole === 'cook' && (
              <span className="px-1.5 py-0.5 rounded-full bg-accent/10 text-accent text-[10px]" data-testid={`leftover-cook-${planId || meal.meal_id}`}>
                Prepare dinner plus tomorrow's lunch
              </span>
            )}
            {leftoverRole === 'leftover' && (
              <span className="px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground text-[10px]" data-testid={`leftover-lunch-${planId || meal.meal_id}`}>
                Leftover from yesterday
              </span>
            )}
          </div>
          
          {/* Meal name */}
          <p className="font-heading font-semibold text-sm truncate" title={meal.name}>
            {meal.name}
          </p>

          {/* Macro row */}
          <div className="flex items-center gap-2 text-[11px] mt-0.5">
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{calories}</span> cal
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{protein}g</span> P
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{carbs}g</span> C
            </span>
            <span className="text-muted-foreground">
              <span className="font-mono font-medium text-foreground">{fat}g</span> F
            </span>
          </div>
        </div>

        {/* Right: Action buttons - vertical stack */}
        <div className="flex flex-col justify-center gap-1">
          {variant === 'home' ? (
            /* HOME VARIANT: Log/Undo, Skip, Favorite */
            <>
              {/* Log or Undo button - changes based on status */}
              {isLogged ? (
                /* Undo Log button - shown when meal is logged */
                <button
                  onClick={(e) => handleActionClick(e, onUndo)}
                  disabled={isLoading.undo}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all hover:bg-yellow-500/20 bg-yellow-500/10"
                  data-testid={`undo-${planId || meal.meal_id}`}
                  title="Undo log"
                >
                  {isLoading.undo ? (
                    <span className="w-3.5 h-3.5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <RotateCcw size={14} className="text-yellow-500" />
                  )}
                  <span className="text-[10px] text-yellow-600">Undo</span>
                </button>
              ) : (
                /* Log button - shown when meal is not logged */
                <button
                  onClick={(e) => handleActionClick(e, onLog)}
                  disabled={isLoading.log}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all hover:bg-accent/20 bg-secondary/40"
                  data-testid={`log-${planId || meal.meal_id}`}
                  title="Log meal"
                >
                  {isLoading.log ? (
                    <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus size={14} className="text-accent" />
                  )}
                  <span className="text-[10px] text-muted-foreground">Log</span>
                </button>
              )}

              {/* Skip or Remove button - Remove shown for skipped and logged meals */}
              {isSkipped || isLogged ? (
                /* Remove button - shown when meal is skipped OR logged (to allow corrections) */
                <button
                  onClick={(e) => handleActionClick(e, onRemove)}
                  disabled={isLoading.remove}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all hover:bg-red-500/20 bg-red-500/10"
                  data-testid={`remove-${planId || meal.meal_id}`}
                  title="Remove from plan"
                >
                  {isLoading.remove ? (
                    <span className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <X size={14} className="text-red-400" />
                  )}
                  <span className="text-[10px] text-red-500">Remove</span>
                </button>
              ) : (
                /* Skip button - shown when meal is pending (not logged/skipped) */
                <button
                  onClick={(e) => handleActionClick(e, onSkip)}
                  disabled={isLoading.skip}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all hover:bg-secondary/60 bg-secondary/40"
                  data-testid={`skip-${planId || meal.meal_id}`}
                  title="Skip meal"
                >
                  {isLoading.skip ? (
                    <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <X size={14} className="text-accent" />
                  )}
                  <span className="text-[10px] text-muted-foreground">Skip</span>
                </button>
              )}
            </>
          ) : (
            /* PLAN VARIANT: Swap, Remove, Favorite */
            <>
              {/* Swap button */}
              <button
                onClick={(e) => handleActionClick(e, onSwap)}
                disabled={isLoading.swap || isLogged}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all ${
                  isLogged 
                    ? 'opacity-40 cursor-not-allowed bg-secondary/30' 
                    : 'hover:bg-secondary/60 bg-secondary/40'
                }`}
                data-testid={`swap-${planId || meal.meal_id}`}
                title="Swap meal"
              >
                {isLoading.swap ? (
                  <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                ) : (
                  <RefreshCw size={14} className="text-accent" />
                )}
                <span className="text-[10px] text-muted-foreground">Swap</span>
              </button>

              {/* Remove button */}
              <button
                onClick={(e) => handleActionClick(e, onRemove)}
                disabled={isLoading.remove}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all hover:bg-red-500/10 bg-secondary/40"
                data-testid={`remove-${planId || meal.meal_id}`}
                title="Remove meal"
              >
                {isLoading.remove ? (
                  <span className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <X size={14} className="text-red-400" />
                )}
                <span className="text-[10px] text-muted-foreground">Remove</span>
              </button>
            </>
          )}

          {/* Favorite button - same for both variants */}
          <button
            onClick={(e) => handleActionClick(e, onFavorite)}
            disabled={isLoading.favorite}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all ${
              isFavorite ? 'bg-red-500/10' : 'hover:bg-secondary/60 bg-secondary/40'
            }`}
            data-testid={`favorite-${planId || meal.meal_id}`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isLoading.favorite ? (
              <span className="w-3.5 h-3.5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            ) : (
              <Heart 
                size={14} 
                className={isFavorite ? 'text-red-500' : 'text-accent'} 
                fill={isFavorite ? 'currentColor' : 'none'}
              />
            )}
            <span className="text-[10px] text-muted-foreground">Favorite</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default MealCard;
